import { ReactElement, ReactNode, HTMLAttributes, useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { Button } from '@revenuemarket/cds';
import { mixpanelTrack, useIsMobile } from '@revenuemarket/utils';
import { Partner } from '@revenuemarket/utils/partners/getPartnerByHostname';
import Head from 'next/head';
import Image from 'next/image';
import ContentWriterImage from '~assets/images/finance/common/contentwriter.svg';
import DataEncryptionImage from '~assets/images/finance/common/dataencryption.svg';
import ScienceImage from '~assets/images/finance/common/science.svg';
import useIntroAutoCapture from '~hooks/useIntroAutoCapture';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

const PartnerLanding = ({ title, value: _value }: { title: string; value: Partner }) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCaptureMode, setIsCaptureMode] = useState(false);

  const isWtoptax = title === '세무법인세일택스';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    setIsCaptureMode(params.get('download') === '1');
  }, []);

  useIntroAutoCapture({
    containerRef,
    getFileName: () => `${title} 고객안내문`,
  });

  const secondDescription = (() => {
    return (
      <>
        앞으로 전송된 결산·신고 자료는
        {isMobile ? <br /> : ' '}
        추가 요청드리지 않을 예정입니다.
      </>
    );
  })();

  return (
    <>
      <Head>
        <title>{title} 고객 전용 자료 전송 간편화 서비스</title>
        <meta name="description" content={`${title} 고객 전용으로 자료 전송이 간편화 됩니다.`} />
      </Head>

      <Container ref={containerRef} capture={isCaptureMode}>
        <HeaderSection>
          <TitleContainer>
            <TitleText>{title} 고객 전용</TitleText>
            <TitleText>자료 전송 간편화 서비스</TitleText>
          </TitleContainer>
          <DescriptionText isMobile={isMobile}>
            <span>
              최초 1회 공동인증서 연동을 통해{isMobile ? <br /> : ' '}자료 전송이 간편화 됩니다.
            </span>
            <span>{secondDescription}</span>
          </DescriptionText>
        </HeaderSection>
        <ContentWrapper>
          {/* Header Section */}

          {/* Process Steps Section */}
          <ProcessSection>
            {/* Step 1 */}

            <StepContent isFirst>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber bgColor="#1D4ED8" data-capture-step-number>
                    1
                  </StepNumber>
                  <StepTitle color="#1D4ED8">자료 전송 자동화</StepTitle>
                </StepHeader>
                <StepDescription>
                  <BulletPoint>
                    <StepDescriptionText>
                      한 번 연동, 반복 요청 없이{isMobile ? <br /> : ' '}결산·신고 자료 자동 전송
                    </StepDescriptionText>
                  </BulletPoint>
                  <BulletPoint>
                    <StepDescriptionText>
                      통장, 법인카드, 홈택스, 4대보험{isMobile ? <br /> : ' '}자료 실시간 수집 및
                      조회
                    </StepDescriptionText>
                  </BulletPoint>
                  <BulletPoint>
                    <StepDescriptionText>
                      해외 카드 매입내역, 수출입내역,{isMobile ? <br /> : ' '}PG/마켓플레이스 등
                      자료 지원
                    </StepDescriptionText>
                  </BulletPoint>
                </StepDescription>
              </StepInfoContainer>
              <IconContainer>
                <Image src={ContentWriterImage} alt="자료 전송 자동화" width={138} height={138} />
              </IconContainer>
            </StepContent>

            {/* Step 2 */}

            <StepContent>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber bgColor="#1D4ED8" data-capture-step-number>
                    2
                  </StepNumber>
                  <StepTitle color="#1D4ED8">실시간 자금관리 기능</StepTitle>
                </StepHeader>
                <StepDescription>
                  <BulletPoint>
                    <StepDescriptionText>
                      현금흐름, 자금일보, 매출·매입 정산 등{isMobile ? <br /> : ' '}편리한 자금관리
                      기능 제공
                    </StepDescriptionText>
                  </BulletPoint>
                  <BulletPoint>
                    <StepDescriptionText>
                      홈택스 로그인 없이 세금계산서{isMobile ? <br /> : ' '}단건, 예약, 대량 발급
                      가능
                    </StepDescriptionText>
                  </BulletPoint>
                  <BulletPoint>
                    <StepDescriptionText>
                      하나은행, 국민은행 로그인 없이{isMobile ? <br /> : ' '} 계좌 이체 가능
                    </StepDescriptionText>
                  </BulletPoint>
                </StepDescription>
              </StepInfoContainer>
              <IconContainer>
                <Image
                  src={DataEncryptionImage}
                  alt="실시간 자금관리 기능"
                  width={138}
                  height={138}
                />
              </IconContainer>
            </StepContent>

            {/* Step 3 */}

            <StepContent isLast>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber bgColor="#1D4ED8" data-capture-step-number>
                    3
                  </StepNumber>
                  <StepTitle color="#1D4ED8">요금 안내</StepTitle>
                </StepHeader>
                <StepDescription>
                  <BulletPoint>
                    <StepDescriptionText>
                      {isWtoptax
                        ? `고객 전용 요금 무료`
                        : `고객 전용 서비스로 모든 기능들은${
                            isMobile ? <br /> : ' '
                          } 평생 무료로 제공`}
                    </StepDescriptionText>
                  </BulletPoint>
                  <BulletPoint>
                    <StepDescriptionText>
                      {isWtoptax
                        ? `제1금융권 보안 기준 충족`
                        : `6,500여개 법인 기업들이${isMobile ? <br /> : ' '}매일 이용 중`}
                    </StepDescriptionText>
                  </BulletPoint>
                </StepDescription>
              </StepInfoContainer>
              <IconContainer>
                <Image src={ScienceImage} alt="요금 안내" width={138} height={138} />
              </IconContainer>
            </StepContent>
          </ProcessSection>

          {/* Button Section */}
          <ButtonSection data-html2canvas-ignore>
            <CTAButton
              onClick={() => {
                mixpanelTrack(MIXPANEL_EVENT.PARTNERS.CLICK_PARTNERS_START_BUTTON, {
                  url: window.location.pathname,
                  title,
                });
                window.open(`/auth/signup`, '_self', 'noopener,noreferrer');
              }}
              style={{
                width: 'fit-content',
                marginTop: 30,
              }}
            >
              지금 시작하기
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  marginLeft: 10,
                }}
              >
                <path
                  d="M1.5 12C1.5 18.1 6.4 23 12.5 23C18.6 23 23.5 18.1 23.5 12C23.5 5.9 18.6 1 12.5 1C6.4 1 1.5 5.9 1.5 12ZM7 12C7 11.4 7.4 11 8 11H14.6L12.3 8.7C11.9 8.3 11.9 7.7 12.3 7.3C12.7 6.9 13.3 6.9 13.7 7.3L17.7 11.3C18.1 11.7 18.1 12.3 17.7 12.7L13.7 16.7C13.5 16.9 13.3 17 13 17C12.7 17 12.5 16.9 12.3 16.7C11.9 16.3 11.9 15.7 12.3 15.3L14.6 13H8C7.5 13 7 12.6 7 12Z"
                  fill="white"
                />
              </svg>
            </CTAButton>
          </ButtonSection>
        </ContentWrapper>
      </Container>
    </>
  );
};

// 레이아웃을 완전히 제거하여 독립적인 페이지로 만듦
PartnerLanding.getLayout = function getLayout(page: ReactElement) {
  return page;
};

// Styled Components
const Container = styled.div<{ capture?: boolean }>`
  width: ${(props) => (props.capture ? '1190px' : '100%')};
  height: ${(props) => (props.capture ? '1640px' : 'auto')};
  min-height: ${(props) => (props.capture ? '1640px' : '100vh')};
  background: linear-gradient(0deg, #fcfcfe 0%, #fcfcfe 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 114px 60px 60px;
  overflow: hidden;

  @media (max-width: 1040px) {
    padding: 114px 60px 60px;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1054px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;

  @media (max-width: 1040px) {
    max-width: 650px;
  }

  @media (max-width: 768px) {
    max-width: 375px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 649px;
  margin-bottom: 78px;

  @media (max-width: 1040px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 54px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 708px;

  @media (max-width: 1040px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TitleText = styled.div`
  font-family: 'Pretendard';
  font-weight: 800;
  font-size: 60px;
  line-height: 120%;
  text-align: center;
  letter-spacing: -0.02em;
  color: #1d4ed8;

  @media (max-width: 1040px) {
    font-size: 48px;
    line-height: 140%;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 130%;
  }
`;

const DescriptionText = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.isMobile ? '24px' : '0')};
  width: 649px;
  font-family: 'Pretendard';
  font-weight: 500;
  font-size: 24px;
  line-height: 140%;
  text-align: center;
  letter-spacing: -0.02em;
  color: #505155;

  @media (max-width: 1040px) {
    width: 100%;
    font-size: 20px;
    line-height: 160%;
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    line-height: 150%;
  }
`;

const ProcessSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 1054px;

  @media (max-width: 1040px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StepContent = styled.div<{ isFirst?: boolean; isLast?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 76px 82px 76px 68px;
  gap: 52px;
  width: 100%;
  background: #ffffff;
  border-radius: ${(props) =>
    props.isFirst ? '32px 32px 0px 0px' : props.isLast ? '0px 0px 32px 32px' : '0px'};

  @media (max-width: 1040px) {
    padding: 60px 50px 60px 40px;
    gap: 40px;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    padding: 30px;
    gap: 30px;
  }
`;

const StepInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  flex: 1;

  @media (max-width: 1040px) {
    gap: 24px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StepHeader = styled.div`
  display: grid;
  grid-template-columns: 32px auto;
  align-items: center;
  padding: 0px;
  gap: 14px;
`;

// NOTE: 캡처시 배경의 위치가 어긋나는 문제를 해결하기 위해 svg 사용
const StepNumber = ({
  bgColor,
  children,
  ...rest
}: { bgColor: string; children: ReactNode } & HTMLAttributes<HTMLDivElement>) => {
  const label =
    typeof children === 'string' || typeof children === 'number' ? String(children).trim() : '';
  const svg = `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>\n  <circle cx='16' cy='16' r='16' fill='${bgColor}' />\n  <text x='16' y='16' text-anchor='middle' dominant-baseline='central' dy='0.08em' font-family='Pretendard' font-weight='800' font-size='20' fill='#FFFFFF'>${label}</text>\n</svg>`;
  const dataUrl = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  return (
    <div
      {...rest}
      style={{
        width: 32,
        height: 32,
        backgroundImage: dataUrl,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
        display: 'block',
      }}
    />
  );
};

const StepTitle = styled.div<{ color: string }>`
  font-family: 'Pretendard';
  font-weight: 800;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: -0.02em;
  color: ${(props) => props.color};
  white-space: nowrap;

  @media (max-width: 1040px) {
    font-size: 28px;
    line-height: 120%;
    white-space: normal;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 130%;
  }
`;

const StepDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;

  @media (max-width: 1040px) {
    gap: 16px;
  }
`;

const BulletPoint = styled.div`
  position: relative;
  padding-left: 20px;
  width: 100%;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #89929d;
    font-size: 26px;
    line-height: 100%;

    @media (max-width: 1040px) {
      font-size: 22px;
    }

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
`;

const StepDescriptionText = styled.div`
  font-family: 'Pretendard';
  font-weight: 500;
  font-size: 26px;
  line-height: 100%;
  letter-spacing: -0.02em;
  color: #505155;

  @media (max-width: 1040px) {
    font-size: 22px;
    line-height: 130%;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 140%;
  }
`;

const IconContainer = styled.div`
  margin: 0 auto;
  width: 138px;
  height: 138px;
  flex-shrink: 0;

  @media (max-width: 1040px) {
    width: 120px;
    height: 120px;
  }

  @media (max-width: 768px) {
    align-self: flex-start;
    margin: 0;
    width: 100px;
    height: 100px;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const CTAButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 19px 36px;
  gap: 10px;
  width: 223px;
  height: 68px;
  background: #1d4ed8;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
  border-radius: 40px;
  border: none;

  font-family: 'Pretendard';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  letter-spacing: -0.4px;
  color: #ffffff;

  &:hover {
    background: #1d2088;
  }
`;

// 인증 없이 접근 가능한 공개 페이지로 설정
export const getServerSideProps = async () => {
  return {
    props: {},
  };
};

export default PartnerLanding;
